import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import {
  Close,
  Save,
  Person,
  Business,
  Add,
  Delete,
  SwapHoriz,
  SolarPower,
  EvStation,
} from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import {
  CLIENT_TYPES,
  ENTRY_CHANNELS,
  ENTRY_CHANNEL_LABELS,
  CLIENT_TAGS,
  CLIENT_TAG_LABELS,
} from "../../utils/models/clientModel";
import { CONTACT_TYPES } from "../../utils/models/contactModel";
import { createCompany } from "../../utils/models/companyModel";
import ContactForm from "../contacts/ContactForm";

const ClientForm = ({ client, onSave, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { add: addClient, update: updateClient } = useAuditFirestore("clients");
  const { add: addContact, update: updateContact } =
    useAuditFirestore("contacts");
  const { add: addCompany } = useAuditFirestore("companies");
  const { data: contacts, refresh: refreshContacts } =
    useCollection("contacts");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openContactDialog, setOpenContactDialog] = useState(false);

    const initializeAddresses = () => {
    if (client?.addresses && client.addresses.length > 0) {
      return client.addresses;
    }
    return [
      {
        street: client?.address?.street || "",
        city: client?.address?.city || "",
        state: client?.address?.state || "",
        zipCode: client?.address?.zipCode || "",
        country: client?.address?.country || "España",
        cups: client?.cups || "",
        cupsGas: client?.cupsGas || "",
        referenciaCatastral: client?.referenciaCatastral || "",
        notes: "",
        hasInvoice: client?.hasInvoice || false,
        invoiceLink: client?.invoiceLink || "",
      },
    ];
  };

  const [formData, setFormData] = useState({
    type: client?.type || CLIENT_TYPES.INDIVIDUAL,
    tags: client?.tags || [],
    firstName: client?.firstName || "",
    lastName: client?.lastName || "",
    dni: client?.dni || "",
    companyName: client?.companyName || "",
    taxId: client?.taxId || "",
    contactName: client?.contactName || "",
    representativeName: client?.representativeName || "",
    representativePhone: client?.representativePhone || "",
    representativeEmail: client?.representativeEmail || "",
    representativeDni: client?.representativeDni || "",
    email: client?.email || "",
    phone: client?.phone || "",
    iban: client?.iban || "",
    addresses: initializeAddresses(),
    files: client?.files || [],
    entryChannel: client?.entryChannel || ENTRY_CHANNELS.WEB,
    entryChannelContactId: client?.entryChannelContactId || "",
    contactId: client?.contactId || "",
    companyId: client?.companyId || "",
    notes: client?.notes || "",
    isActive: client?.isActive !== false,
  });

  const isIndividual = formData.type === CLIENT_TYPES.INDIVIDUAL;
  const isCompany = formData.type === CLIENT_TYPES.COMPANY;

  const showChannelContactSelector =
    formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL ||
    formData.entryChannel === ENTRY_CHANNELS.EPC;

  const getFilteredContacts = () => {
    if (!contacts) return [];
    if (formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL) {
      return contacts.filter(
        (c) =>
          c.contactType === CONTACT_TYPES.COMMERCIAL && c.isActive !== false,
      );
    }
    if (formData.entryChannel === ENTRY_CHANNELS.EPC) {
      return contacts.filter(
        (c) => c.contactType === CONTACT_TYPES.EPC && c.isActive !== false,
      );
    }
    return [];
  };

  const getSelectedContact = () => {
    if (!formData.entryChannelContactId || !contacts) return null;
    return (
      contacts.find((c) => c.id === formData.entryChannelContactId) || null
    );
  };

  useEffect(() => {
    setFormData((prev) => ({ ...prev, entryChannelContactId: "" }));
  }, [formData.entryChannel]);

  useEffect(() => {
    setFormData({
      type: client?.type || CLIENT_TYPES.INDIVIDUAL,
      tags: client?.tags || [],
      firstName: client?.firstName || "",
      lastName: client?.lastName || "",
      dni: client?.dni || "",
      companyName: client?.companyName || "",
      taxId: client?.taxId || "",
      contactName: client?.contactName || "",
      representativeName: client?.representativeName || "",
      representativePhone: client?.representativePhone || "",
      representativeEmail: client?.representativeEmail || "",
      representativeDni: client?.representativeDni || "",
      email: client?.email || "",
      phone: client?.phone || "",
      iban: client?.iban || "",
      addresses: initializeAddresses(),
      files: client?.files || [],
      entryChannel: client?.entryChannel || ENTRY_CHANNELS.WEB,
      entryChannelContactId: client?.entryChannelContactId || "",
      contactId: client?.contactId || "",
      companyId: client?.companyId || "",
      notes: client?.notes || "",
      isActive: client?.isActive !== false,
    });
  }, [client]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError("");
  };

  const handleTagToggle = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const handleAddressChange = (index, field, value) => {
    const newAddresses = [...formData.addresses];
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    setFormData((prev) => ({ ...prev, addresses: newAddresses }));
  };

    const handleAddAddress = () => {
    setFormData((prev) => ({
      ...prev,
      addresses: [
        ...prev.addresses,
        {
          street: "",
          city: "",
          state: "",
          zipCode: "",
          country: "España",
          cups: "",
          cupsGas: "",
          referenciaCatastral: "",
          notes: "",
          hasInvoice: false,
          invoiceLink: "",
        },
      ],
    }));
  };

  const handleRemoveAddress = (index) => {
    const newAddresses = formData.addresses.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, addresses: newAddresses }));
  };

  const handleClientType = (_, value) => {
    if (!value) return;
    setFormData((prev) => ({ ...prev, type: value }));
  };

  const handleChannelContactChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      entryChannelContactId: newValue ? newValue.id : "",
    }));
  };

  const handleContactCreated = async (newContactId) => {
    if (refreshContacts) await refreshContacts();
    setFormData((prev) => ({ ...prev, entryChannelContactId: newContactId }));
    setOpenContactDialog(false);
  };

  const handleFileChange = (index, field, value) => {
    const newFiles = [...formData.files];
    newFiles[index] = { ...newFiles[index], [field]: value };
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleAddFile = () => {
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, { name: "", url: "" }],
    }));
  };

  const handleRemoveFile = (index) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCompany && !formData.companyName) {
      setError("El nombre de empresa es obligatorio");
      return;
    }
    if (isIndividual && (!formData.firstName || !formData.lastName)) {
      setError("Nombre y apellidos son obligatorios");
      return;
    }
    if (!formData.addresses || formData.addresses.length === 0) {
      setError("Se requiere al menos una dirección");
      return;
    }

    const validFiles = formData.files.filter(
      (file) => file.name.trim() !== "" && file.url.trim() !== "",
    );
    const primaryAddress = formData.addresses[0];

    setLoading(true);
    setError("");

    try {
      let contactId = formData.contactId;
      let companyId = formData.companyId;

      if (!contactId) {
        if (isCompany && !companyId) {
          const companyData = createCompany({
            name: formData.companyName,
            taxId: formData.taxId || "",
            email: formData.email || "",
            phone: formData.phone || "",
            address: primaryAddress,
            notes: `Empresa creada desde cliente: ${formData.companyName}`,
          });
          const newCompany = await addCompany(companyData);
          companyId = newCompany?.id || newCompany;
        }

        const contactData = {
          contactType: CONTACT_TYPES.CLIENT,
          firstName: isIndividual ? formData.firstName : "",
          lastName: isIndividual ? formData.lastName : "",
          email: formData.email,
          phone: formData.phone,
          dni: isIndividual ? formData.dni : "",
          contactName: isCompany ? formData.contactName : "",
          clientType: formData.type,
          companyName: isCompany ? formData.companyName : "",
          companyId: isCompany ? companyId : "",
          companyTaxId: isCompany ? formData.taxId : "",
          representativeName: isCompany ? formData.representativeName : "",
          representativePhone: isCompany ? formData.representativePhone : "",
          representativeEmail: isCompany ? formData.representativeEmail : "",
          representativeDni: isCompany ? formData.representativeDni : "",
          address: primaryAddress,
          notes: `Contacto creado automáticamente desde cliente${isCompany ? " empresa" : " particular"}`,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const newContact = await addContact(contactData);
        contactId = newContact?.id || newContact;
      } else {
        const contactData = {
          contactType: CONTACT_TYPES.CLIENT,
          firstName: isIndividual ? formData.firstName : "",
          lastName: isIndividual ? formData.lastName : "",
          email: formData.email,
          phone: formData.phone,
          dni: isIndividual ? formData.dni : "",
          contactName: isCompany ? formData.contactName : "",
          clientType: formData.type,
          companyName: isCompany ? formData.companyName : "",
          companyId: isCompany ? companyId : "",
          companyTaxId: isCompany ? formData.taxId : "",
          representativeName: isCompany ? formData.representativeName : "",
          representativePhone: isCompany ? formData.representativePhone : "",
          representativeEmail: isCompany ? formData.representativeEmail : "",
          representativeDni: isCompany ? formData.representativeDni : "",
          address: primaryAddress,
          notes: formData.notes,
          updatedAt: new Date().toISOString(),
        };
        await updateContact(contactId, contactData);
      }

      const clientData = {
        type: formData.type,
        tags: formData.tags,
        firstName: isIndividual ? formData.firstName : "",
        lastName: isIndividual ? formData.lastName : "",
        dni: isIndividual ? formData.dni : "",
        contactName: isCompany ? formData.contactName : "",
        companyName: isCompany ? formData.companyName : "",
        taxId: isCompany ? formData.taxId : "",
        representativeName: isCompany ? formData.representativeName : "",
        representativePhone: isCompany ? formData.representativePhone : "",
        representativeEmail: isCompany ? formData.representativeEmail : "",
        representativeDni: isCompany ? formData.representativeDni : "",
        email: formData.email,
        phone: formData.phone,
        iban: formData.iban,
        addresses: formData.addresses,
        address: primaryAddress,
        cups: primaryAddress.cups || "",
        cupsGas: primaryAddress.cupsGas || "",
        referenciaCatastral: primaryAddress.referenciaCatastral || "",
        hasInvoice: primaryAddress.hasInvoice || false,
        invoiceLink: primaryAddress.invoiceLink || "",
        files: validFiles,
        entryChannel: formData.entryChannel,
        entryChannelContactId: formData.entryChannelContactId,
        contactId: contactId,
        companyId: isCompany ? companyId : null,
        notes: formData.notes,
        isActive: formData.isActive,
        updatedAt: new Date().toISOString(),
      };

      let savedClient;
      if (client?.id) {
        await updateClient(client.id, clientData);
        savedClient = { id: client.id, ...clientData };
      } else {
        const newClient = await addClient({
          ...clientData,
          createdAt: new Date().toISOString(),
        });
        const newClientId = newClient?.id || newClient;
        savedClient = { id: newClientId, ...clientData };
        if (contactId && newClientId && !formData.contactId) {
          await updateContact(contactId, { clientId: newClientId });
        }
      }

      onSave(savedClient);
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.default,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
  };

  const formatContactOption = (contact) => {
    if (!contact) return "";
    const fullName =
      `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
    const email = contact.email || "";
    return email ? `${fullName} (${email})` : fullName;
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: { xs: "16px", sm: "24px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        {/* Cabecera */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: { xs: 3, sm: 4 },
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}
            >
              {client?.id ? "Editar cliente" : "Nuevo cliente"}
            </Typography>
            {!client?.id && (
              <Typography
                variant="caption"
                color="primary.main"
                fontWeight={500}
                sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
              >
                Se creará automáticamente el contacto
                {isCompany ? " y la empresa" : ""} asociado
                {isCompany ? "s" : ""}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onCancel}
            size="small"
            sx={{
              background: theme.palette.background.paper,
              border: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "#e2e8f0",
            }}
          >
            <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: { xs: 2, sm: 3 },
              borderRadius: "12px",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* ETIQUETAS - ENCIMA DE TIPO DE CLIENTE */}
          <Typography sx={{ ...sectionTitleSx, mt: 0 }}>
            Etiquetas del cliente
          </Typography>
          <FormGroup row sx={{ gap: 1, flexWrap: "wrap", mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.tags.includes(CLIENT_TAGS.CAMBIO_COMERCIALIZADORA)}
                  onChange={() => handleTagToggle(CLIENT_TAGS.CAMBIO_COMERCIALIZADORA)}
                  icon={<SwapHoriz />}
                  checkedIcon={<SwapHoriz />}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&.Mui-checked": {
                      color: theme.palette.warning.main,
                    },
                  }}
                />
              }
              label="Cambio de comercializadora"
              sx={{ mr: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.tags.includes(CLIENT_TAGS.FOTOVOLTAICA)}
                  onChange={() => handleTagToggle(CLIENT_TAGS.FOTOVOLTAICA)}
                  icon={<SolarPower />}
                  checkedIcon={<SolarPower />}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&.Mui-checked": {
                      color: theme.palette.success.main,
                    },
                  }}
                />
              }
              label="Fotovoltaica"
              sx={{ mr: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.tags.includes(CLIENT_TAGS.CARGADOR_ELECTRICO)}
                  onChange={() => handleTagToggle(CLIENT_TAGS.CARGADOR_ELECTRICO)}
                  icon={<EvStation />}
                  checkedIcon={<EvStation />}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&.Mui-checked": {
                      color: theme.palette.info.main,
                    },
                  }}
                />
              }
              label="Cargador eléctrico"
              sx={{ mr: 0 }}
            />
          </FormGroup>

          {/* Tipo de cliente */}
          <Typography sx={sectionTitleSx}>Tipo de cliente</Typography>
          <ToggleButtonGroup
            exclusive
            value={formData.type}
            onChange={handleClientType}
            size={isMobile ? "small" : "medium"}
            sx={{
              height: { xs: 40, sm: 44 },
              background: theme.palette.background.paper,
              borderRadius: "12px",
              mb: 2,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <ToggleButton
              value={CLIENT_TYPES.INDIVIDUAL}
              sx={{ flex: 1, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
            >
              <Person sx={{ mr: 1, fontSize: { xs: 16, sm: 18 } }} /> Particular
            </ToggleButton>
            <ToggleButton
              value={CLIENT_TYPES.COMPANY}
              sx={{ flex: 1, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
            >
              <Business sx={{ mr: 1, fontSize: { xs: 16, sm: 18 } }} /> Empresa
            </ToggleButton>
          </ToggleButtonGroup>

          {isIndividual && (
            <>
              <Typography sx={sectionTitleSx}>Datos personales</Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                  required
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Apellidos"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                  required
                />
                <TextField
                  fullWidth
                  size="small"
                  label="DNI / NIE"
                  name="dni"
                  value={formData.dni}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                />
              </Box>
            </>
          )}

          {isCompany && (
            <>
              <Typography sx={sectionTitleSx}>Datos de empresa</Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre de empresa"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 2,
                    minWidth: { xs: "100%", sm: "300px" },
                  }}
                  required
                />
                <TextField
                  fullWidth
                  size="small"
                  label="CIF / NIF"
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                />
              </Box>
              <Typography sx={sectionTitleSx}>Representante</Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre del representante"
                  name="representativeName"
                  value={formData.representativeName}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "250px" },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="DNI/NIF del representante"
                  name="representativeDni"
                  value={formData.representativeDni}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                  width: "100%",
                  mt: 2,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Teléfono"
                  name="representativePhone"
                  value={formData.representativePhone}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="Email"
                  name="representativeEmail"
                  value={formData.representativeEmail}
                  onChange={handleChange}
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "250px" },
                  }}
                />
              </Box>
            </>
          )}

          <Typography sx={sectionTitleSx}>Datos de contacto</Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {isCompany && (
              <Box sx={{ width: "100%", mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre de la persona de contacto"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  sx={{ ...fieldSx, maxWidth: { xs: "100%", sm: "400px" } }}
                />
              </Box>
            )}
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "250px" },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Teléfono"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "250px" },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="IBAN"
              name="iban"
              value={formData.iban}
              onChange={handleChange}
              placeholder="ES00 0000 0000 0000 0000 0000"
              sx={{
                ...fieldSx,
                flex: 2,
                minWidth: { xs: "100%", sm: "300px" },
              }}
            />
          </Box>

          <Typography sx={sectionTitleSx}>Canal de entrada</Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
              width: "100%",
              alignItems: "flex-start",
            }}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ flex: 1, minWidth: { xs: "100%", sm: "300px" } }}
            >
              <InputLabel>Canal</InputLabel>
              <Select
                name="entryChannel"
                value={formData.entryChannel}
                onChange={handleChange}
                label="Canal"
                sx={{
                  borderRadius: "12px",
                  background: theme.palette.background.paper,
                  height: { xs: 40, sm: 44 },
                }}
              >
                {Object.entries(ENTRY_CHANNEL_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {showChannelContactSelector && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
              >
                <Autocomplete
                  value={getSelectedContact()}
                  onChange={handleChannelContactChange}
                  options={getFilteredContacts()}
                  getOptionLabel={(option) => formatContactOption(option)}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL
                          ? "Seleccionar Comercial"
                          : "Seleccionar EPC"
                      }
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.paper,
                          height: { xs: 40, sm: 44 },
                        },
                      }}
                    />
                  )}
                  noOptionsText={
                    formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL
                      ? "No hay comerciales disponibles"
                      : "No hay EPCs disponibles"
                  }
                  sx={{ flex: 1 }}
                />
                {formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL && (
                  <Tooltip title="Añadir nuevo comercial">
                    <IconButton
                      onClick={() => setOpenContactDialog(true)}
                      sx={{
                        width: { xs: 40, sm: 44 },
                        height: { xs: 40, sm: 44 },
                        borderRadius: "12px",
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>

          {/* DIRECCIONES CON FACTURA INTEGRADA */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: { xs: 4, sm: 5 },
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Typography sx={{ ...sectionTitleSx, mt: 0, mb: 0 }}>
              Direcciones{" "}
              {formData.addresses.length > 1 &&
                `(${formData.addresses.length})`}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={handleAddAddress}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(0, 175, 163, 0.1)"
                      : "rgba(0, 95, 101, 0.05)",
                },
              }}
            >
              Añadir dirección
            </Button>
          </Box>

          {formData.addresses.map((address, index) => (
            <Box
              key={index}
              sx={{
                mb: index < formData.addresses.length - 1 ? 4 : 0,
                p: { xs: 2, sm: 3 },
                borderRadius: "16px",
                border: "1px solid",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0, 95, 101, 0.1)",
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0, 95, 101, 0.02)",
                position: "relative",
              }}
            >
              {/* Título y botón eliminar */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  }}
                >
                  Dirección {index + 1}
                </Typography>
                {formData.addresses.length > 1 && (
                  <IconButton
                    onClick={() => handleRemoveAddress(index)}
                    size="small"
                    sx={{
                      color: theme.palette.error.main,
                      border: "1px solid",
                      borderColor: theme.palette.error.main,
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: theme.palette.error.main,
                        color: "white",
                      },
                    }}
                  >
                    <Delete sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>

              {/* Campos de dirección */}
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Calle"
                  value={address.street}
                  onChange={(e) =>
                    handleAddressChange(index, "street", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "300px" },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Ciudad"
                  value={address.city}
                  onChange={(e) =>
                    handleAddressChange(index, "city", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "300px" },
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                  mt: 2,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Provincia"
                  value={address.state}
                  onChange={(e) =>
                    handleAddressChange(index, "state", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Código postal"
                  value={address.zipCode}
                  onChange={(e) =>
                    handleAddressChange(index, "zipCode", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="País"
                  value={address.country}
                  onChange={(e) =>
                    handleAddressChange(index, "country", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                />
              </Box>

                            {/* CUPS LUZ, CUPS GAS y Referencia Catastral */}
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1.5, sm: 2.5 },
                  flexWrap: "wrap",
                  mt: 2,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="CUPS Luz"
                  value={address.cups}
                  onChange={(e) =>
                    handleAddressChange(index, "cups", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "250px" },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="CUPS Gas"
                  value={address.cupsGas || ""}
                  onChange={(e) =>
                    handleAddressChange(index, "cupsGas", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "250px" },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Referencia Catastral"
                  value={address.referenciaCatastral}
                  onChange={(e) =>
                    handleAddressChange(
                      index,
                      "referenciaCatastral",
                      e.target.value,
                    )
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "250px" },
                  }}
                />
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Notas de la dirección"
                value={address.notes || ""}
                onChange={(e) =>
                  handleAddressChange(index, "notes", e.target.value)
                }
                sx={{
                  ...fieldSx,
                  mt: 2,
                  "& .MuiInputBase-inputMultiline": {
                    paddingTop: "12px",
                    paddingBottom: "12px",
                  },
                }}
              />
              {/* FACTURA DE LA DIRECCIÓN */}
              <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                Factura de luz
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={address.hasInvoice || false}
                    onChange={(e) =>
                      handleAddressChange(index, "hasInvoice", e.target.checked)
                    }
                  />
                }
                label="¿Tiene factura de luz?"
                sx={{ mb: 2 }}
              />
              {address.hasInvoice && (
                <TextField
                  fullWidth
                  size="small"
                  label="Enlace a la factura"
                  value={address.invoiceLink || ""}
                  onChange={(e) =>
                    handleAddressChange(index, "invoiceLink", e.target.value)
                  }
                  placeholder="https://drive.google.com/..."
                  helperText="Pega aquí el enlace a la factura (Google Drive, Dropbox, etc.)"
                  sx={fieldSx}
                />
              )}
            </Box>
          ))}

          {/* SECCIÓN DE ARCHIVOS */}
          <Typography sx={sectionTitleSx}>Archivos</Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              width: "100%",
            }}
          >
            {formData.files.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: "center",
                  width: "100%",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <TextField
                  size="small"
                  label="Nombre del archivo"
                  value={file.name}
                  onChange={(e) =>
                    handleFileChange(index, "name", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                />
                <TextField
                  size="small"
                  label="Enlace (URL)"
                  value={file.url}
                  onChange={(e) =>
                    handleFileChange(index, "url", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 2,
                    minWidth: { xs: "100%", sm: "300px" },
                  }}
                />
                <IconButton
                  onClick={() => handleRemoveFile(index)}
                  size="small"
                  sx={{
                    width: { xs: 40, sm: 44 },
                    height: { xs: 40, sm: 44 },
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: theme.palette.error.main,
                      color: "white",
                    },
                  }}
                >
                  <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddFile}
              sx={{
                borderRadius: "12px",
                height: { xs: 40, sm: 44 },
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                alignSelf: "flex-start",
                mt: 1,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(0, 175, 163, 0.1)"
                      : "rgba(0, 95, 101, 0.05)",
                },
              }}
            >
              Añadir archivo
            </Button>
          </Box>

          <Typography sx={sectionTitleSx}>Notas internas</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notas"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                background: theme.palette.background.paper,
              },
            }}
          />

          <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                borderRadius: "12px",
                px: { xs: 2, sm: 3 },
                height: { xs: 40, sm: 44 },
                textTransform: "none",
              }}
              fullWidth={{ xs: true, sm: false }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={<Save />}
              sx={{
                borderRadius: "12px",
                px: { xs: 2, sm: 3 },
                height: { xs: 40, sm: 44 },
                boxShadow: "none",
                textTransform: "none",
              }}
              fullWidth={{ xs: true, sm: false }}
            >
              {loading
                ? "Guardando..."
                : client?.id
                  ? "Actualizar"
                  : "Crear cliente"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Diálogo para crear contacto */}
      <Dialog
        open={openContactDialog}
        onClose={() => setOpenContactDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "24px" },
            maxHeight: "90vh",
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Nuevo Comercial
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <ContactForm
            contact={{ contactType: CONTACT_TYPES.COMMERCIAL }}
            onSave={(savedContact) => {
              handleContactCreated(savedContact?.id || savedContact);
            }}
            onCancel={() => setOpenContactDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientForm;